import { alpha, Box, styled } from '@mui/material';
import { JSX, useState } from 'react';
import {
  Assay, IBiomaterial, IPatient, ISubmitAssayData,
  lbAssays,
} from '@zero-dash/types';
import CustomButton from '@/components/Common/Button';
import CustomTypography from '@/components/Common/Typography';
import SubmitConfirmationModal from './SubmitConfirmationModal';

const BarWrapper = styled(Box)({
  display: 'flex',
  gap: '12px',
  justifyContent: 'space-between',
  padding: '0px 58px',
  alignItems: 'center',
});

const LeftComponentWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const AssayChipsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'left',
  gap: '12px',
  padding: '12px',
  borderRadius: '8px',
  border: `1px solid ${theme.colours.core.yellow150}`,
  backgroundColor: theme.colours.core.white,
  boxShadow: `0 0 15px 0 ${alpha(theme.colours.core.yellow200, 0.2)}`,
  flexWrap: 'wrap',
}));

const ChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 8px',
  backgroundColor: theme.colours.core.grey30,
}));

const GreenCircle = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0 4px',
  height: '20px',
  minWidth: '20px',
  borderRadius: '100px',
  border: `1px solid ${theme.colours.core.green150}`,
  backgroundColor: theme.colours.core.green10,
}));

const ActionButtonsWrapper = styled(Box)({
  display: 'flex',
  gap: '12px',
});

interface IProps {
  onReset: () => void;
  onSubmit: (name?: string) => Promise<void>;
  dataToSubmit: ISubmitAssayData[];
  biomaterials: IBiomaterial[];
  patientRecords: IPatient[];
  canSubmitAssays: boolean;
  resetDisabled: boolean;
}

export default function SubmitActionBar({
  onReset,
  onSubmit,
  dataToSubmit,
  biomaterials,
  patientRecords,
  canSubmitAssays,
  resetDisabled,
}: IProps):JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const getAssaysCount = (): {name: string; count: number }[] => {
    const allAssays: Assay[] = [];
    for (const data of dataToSubmit) {
      allAssays.push(...data.assays);
    }

    return Object.entries(
      allAssays.reduce<Partial<Record<Assay, number>>>((acc, item) => {
        acc[item] = (acc[item] || 0) + 1;
        return acc;
      }, {}),
    )
      .map(([name, count]) => ({
        name: lbAssays[name],
        count,
      }));
  };

  return (
    <>
      <BarWrapper>
        <LeftComponentWrapper>
          <CustomTypography variant="bodyRegular" fontWeight="medium" whiteSpace="nowrap">
            Assigned assays
          </CustomTypography>
          <AssayChipsWrapper>
            {
              getAssaysCount().map((count) => (
                <ChipContainer key={count.name}>
                  <CustomTypography variant="bodySmall" fontWeight="bold">
                    {count.name}
                  </CustomTypography>
                  <GreenCircle>
                    <CustomTypography variant="bodyTiny" fontWeight="medium">
                      {count.count}
                    </CustomTypography>
                  </GreenCircle>
                </ChipContainer>
              ))
            }
          </AssayChipsWrapper>
        </LeftComponentWrapper>
        <ActionButtonsWrapper>
          <CustomButton
            label="Reset all changes"
            variant="subtle"
            onClick={onReset}
            disabled={resetDisabled}
          />
          <CustomButton
            label="Submit samples for processing"
            onClick={() => setModalOpen(true)}
            disabled={canSubmitAssays}
          />
        </ActionButtonsWrapper>
      </BarWrapper>
      <SubmitConfirmationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={async (name) => {
          await onSubmit(name);
          setModalOpen(false);
        }}
        dataToSubmit={dataToSubmit}
        biomaterials={biomaterials}
        patientRecords={patientRecords}
      />
    </>
  );
}
